import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle, Circle, ArrowRight } from 'lucide-react';

export function ProfileCompleteness({ profile, type = 'company' }) {
  const isCompany = type === 'company';
  
  // Define fields and their weights for completeness calculation
  const fields = isCompany ? [
    { key: 'company_name', label: 'Company name', weight: 20, required: true },
    { key: 'country', label: 'Country', weight: 15, required: true },
    { key: 'sector', label: 'Industry sector', weight: 15, required: true },
    { key: 'description', label: 'Company description', weight: 10 },
    { key: 'year_established', label: 'Year established', weight: 10 },
    { key: 'employee_count', label: 'Employee count', weight: 5 },
    { key: 'annual_revenue', label: 'Annual revenue', weight: 10 },
    { key: 'website', label: 'Website', weight: 5 },
    { key: 'contact_phone', label: 'Phone number', weight: 5 },
    { key: 'contact_address', label: 'Business address', weight: 5 },
  ] : [
    { key: 'organization_name', label: 'Organization name', weight: 15, required: true },
    { key: 'funder_type', label: 'Organization type', weight: 10, required: true },
    { key: 'country', label: 'Headquarters country', weight: 10, required: true },
    { key: 'description', label: 'Description', weight: 5 },
    { key: 'regions_served', label: 'Regions of interest', weight: 15, isArray: true },
    { key: 'sectors_focus', label: 'Sector focus', weight: 15, isArray: true },
    { key: 'min_ticket_size', label: 'Min ticket size', weight: 5 },
    { key: 'max_ticket_size', label: 'Max ticket size', weight: 5 },
    { key: 'preferred_tenors', label: 'Preferred tenors', weight: 5, isArray: true },
    { key: 'currency_preferences', label: 'Currency preferences', weight: 5, isArray: true },
    { key: 'contact_email', label: 'Contact email', weight: 5 },
    { key: 'contact_phone', label: 'Contact phone', weight: 5 },
  ];

  // Calculate completion percentage
  const calculateCompletion = () => {
    if (!profile) return { percentage: 0, completedFields: [], missingFields: fields };
    
    let completedWeight = 0;
    const completedFields = [];
    const missingFields = [];

    fields.forEach(field => {
      const value = profile[field.key];
      const isComplete = field.isArray 
        ? Array.isArray(value) && value.length > 0
        : value !== null && value !== undefined && value !== '';
      
      if (isComplete) {
        completedWeight += field.weight;
        completedFields.push(field);
      } else {
        missingFields.push(field);
      }
    });

    return {
      percentage: Math.round(completedWeight),
      completedFields,
      missingFields,
    };
  };

  const { percentage, completedFields, missingFields } = calculateCompletion();

  // Color based on completion
  const getColor = () => {
    if (percentage >= 80) return 'emerald';
    if (percentage >= 50) return 'amber';
    return 'blue';
  };

  const color = getColor();

  if (percentage === 100) {
    return (
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-full">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-emerald-800">Profile Complete!</p>
              <p className="text-sm text-emerald-600">Your profile is fully set up</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-medium text-slate-900">Profile Completeness</p>
            <p className="text-sm text-slate-500">{percentage}% complete</p>
          </div>
          <div className={`text-2xl font-bold text-${color}-600`}>
            {percentage}%
          </div>
        </div>
        
        <Progress 
          value={percentage} 
          className={`h-2 mb-4 [&>div]:bg-${color}-500`}
        />

        {missingFields.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase">Complete these to improve your profile:</p>
            <div className="flex flex-wrap gap-2">
              {missingFields.slice(0, 4).map((field) => (
                <span
                  key={field.key}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs text-slate-600"
                >
                  <Circle className="h-3 w-3" />
                  {field.label}
                </span>
              ))}
              {missingFields.length > 4 && (
                <span className="text-xs text-slate-400">
                  +{missingFields.length - 4} more
                </span>
              )}
            </div>
            <Link to={isCompany ? '/company/onboarding' : '/funder/onboarding'}>
              <Button variant="link" className="px-0 h-auto text-blue-600">
                Complete your profile
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ProfileCompleteness;
